import React, { useEffect, useRef } from 'react';

// Extend Window interface for TypeScript
declare global {
  interface Window {
    MPGWidgets?: {
      init: () => void;
      getWidget: (element: Element | null) => any;
      getAllWidgets: () => any[];
    };
  }
}

interface MPGWidgetProps {
  apiKey: string;
  userObject?: {
    name?: string;
    phoneNumber?: string;
    email?: string;
  };
  widgetId?: string;
  type?: string;
  themeColor1?: string | null;
  themeColor2?: string | null;
  themeTxtColor?: string | null;
  themeButtonTxtColor?: string | null;
  onInitialized?: (detail: { widgetType: string; widgetId: string }) => void;
  onProductSelected?: (product: any) => void;
  onError?: (error: { message: string }) => void;
}

const MPGWidget: React.FC<MPGWidgetProps> = ({
  apiKey,
  userObject = {},
  widgetId = 'mpg-widget',
  type = 'ticker',
  themeColor1,
  themeColor2,
  themeTxtColor,
  themeButtonTxtColor,
  onInitialized,
  onProductSelected,
  onError,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const widgetElementRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    // Wait for MPGWidgets to be available
    const checkAndInit = () => {
      if (!window.MPGWidgets) {
        // Retry if MPGWidgets not yet available
        setTimeout(checkAndInit, 100);
        return;
      }

      if (!containerRef.current) {
        return;
      }

      // Remove any existing widget element first
      if (widgetElementRef.current && widgetElementRef.current.parentNode) {
        widgetElementRef.current.parentNode.removeChild(widgetElementRef.current);
        widgetElementRef.current = null;
      }

      // Create the MPGWidget custom element
      const widgetEl = document.createElement('MPGWidget');
      widgetEl.id = widgetId;
      widgetEl.setAttribute('type', type);
      widgetEl.setAttribute('api-key', apiKey || '');
      widgetEl.setAttribute('user-object', JSON.stringify(userObject));
      
      // Set theme attributes if provided
      if (themeColor1) {
        widgetEl.setAttribute('theme-color1', themeColor1);
      }
      if (themeColor2) {
        widgetEl.setAttribute('theme-color2', themeColor2);
      }
      if (themeTxtColor) {
        widgetEl.setAttribute('theme-txt-color', themeTxtColor);
      }
      if (themeButtonTxtColor) {
        widgetEl.setAttribute('theme-button-txt-color', themeButtonTxtColor);
      }

      // Clear container and append widget element directly
      containerRef.current.innerHTML = '';
      containerRef.current.appendChild(widgetEl);
      widgetElementRef.current = widgetEl;

      // Debug logging
      console.log('MPGWidget created:', {
        element: widgetEl,
        tagName: widgetEl.tagName,
        id: widgetEl.id,
        inDOM: document.contains(widgetEl),
        parent: widgetEl.parentNode,
      });

      // Ensure DOM is updated, then initialize
      requestAnimationFrame(() => {
        // Double-check element is in DOM
        if (document.contains(widgetEl) && window.MPGWidgets) {
          // Verify element can be found by querySelector
          const found = document.querySelectorAll('mpgwidget, MPGWIDGET, MPGWidget');
          console.log('Found widget elements before init:', found.length, Array.from(found));
          
          // Call init - this should find the element
          window.MPGWidgets.init();
          
          // Verify after init
          setTimeout(() => {
            const foundAfter = document.querySelectorAll('mpgwidget, MPGWIDGET, MPGWidget');
            console.log('Found widget elements after init:', foundAfter.length, Array.from(foundAfter));
          }, 100);
        } else {
          console.warn('Widget element not in DOM or MPGWidgets not available');
        }
      });
    };

    // Start checking after a short delay
    const timeoutId = setTimeout(checkAndInit, 100);

    // Set up event listeners
    const handleInitialized = (e: CustomEvent) => {
      console.log('Widget initialized event received:', e.detail);
      onInitialized?.(e.detail);
    };

    const handleProductSelected = (e: CustomEvent) => {
      console.log('Product selected event received:', e.detail);
      onProductSelected?.(e.detail);
    };

    const handleError = (e: CustomEvent) => {
      console.error('Widget error event received:', e.detail);
      onError?.(e.detail);
    };

    document.addEventListener('mpg-widget:initialized', handleInitialized as EventListener);
    document.addEventListener('mpg-widget:product-selected', handleProductSelected as EventListener);
    document.addEventListener('mpg-widget:error', handleError as EventListener);

    // Cleanup
    return () => {
      clearTimeout(timeoutId);
      document.removeEventListener('mpg-widget:initialized', handleInitialized as EventListener);
      document.removeEventListener('mpg-widget:product-selected', handleProductSelected as EventListener);
      document.removeEventListener('mpg-widget:error', handleError as EventListener);
      
      // Remove widget element if it exists
      if (widgetElementRef.current && widgetElementRef.current.parentNode) {
        widgetElementRef.current.parentNode.removeChild(widgetElementRef.current);
        widgetElementRef.current = null;
      }
    };
  }, [apiKey, userObject, widgetId, type, themeColor1, themeColor2, themeTxtColor, themeButtonTxtColor, onInitialized, onProductSelected, onError]);

  // Update widget attributes when props change
  useEffect(() => {
    if (widgetElementRef.current && window.MPGWidgets) {
      widgetElementRef.current.setAttribute('api-key', apiKey || '');
      widgetElementRef.current.setAttribute('user-object', JSON.stringify(userObject));
      
      // Update theme attributes
      if (themeColor1) {
        widgetElementRef.current.setAttribute('theme-color1', themeColor1);
      } else {
        widgetElementRef.current.removeAttribute('theme-color1');
      }
      if (themeColor2) {
        widgetElementRef.current.setAttribute('theme-color2', themeColor2);
      } else {
        widgetElementRef.current.removeAttribute('theme-color2');
      }
      if (themeTxtColor) {
        widgetElementRef.current.setAttribute('theme-txt-color', themeTxtColor);
      } else {
        widgetElementRef.current.removeAttribute('theme-txt-color');
      }
      if (themeButtonTxtColor) {
        widgetElementRef.current.setAttribute('theme-button-txt-color', themeButtonTxtColor);
      } else {
        widgetElementRef.current.removeAttribute('theme-button-txt-color');
      }
      
      // Mark as not initialized to trigger re-initialization
      widgetElementRef.current.dataset.mpgWidgetInitialized = 'false';
      
      // Use a small delay to ensure attribute changes are processed
      requestAnimationFrame(() => {
        if (window.MPGWidgets) {
          window.MPGWidgets.init();
        }
      });
    }
  }, [apiKey, userObject, themeColor1, themeColor2, themeTxtColor, themeButtonTxtColor]);

  return (
    <div className="mt-6">
       <div ref={containerRef} className="mpg-widget-placeholder" />
    </div>
  );
};

export default MPGWidget;